import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Chip,
  Alert,
  AlertTitle,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const ActionsCropImage = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [cropCoords, setCropCoords] = useState({ x1: 0, y1: 0, x2: 100, y2: 100 });
  const [tagName, setTagName] = useState('cropped_image');
  const [imagePath, setImagePath] = useState('');
  const [browsingDisabled, setBrowsingDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const imagePathRef = useRef(null);
  const tagNameRef = useRef(null);
  
  // Image preview states
  const [selectedImagePath, setSelectedImagePath] = useState('');
  const [image, setImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const [selectionDimensions, setSelectionDimensions] = useState({ width: 0, height: 0 });
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [resizedPreview, setResizedPreview] = useState(null);
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [resizeWidth, setResizeWidth] = useState(null);
  const [resizeHeight, setResizeHeight] = useState(null);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const hasManualResizeRef = useRef(false);
  const { x1, y1, x2, y2 } = cropCoords;

  const coerceBoolean = useCallback((value, defaultValue = false) => {
    if (typeof value === 'boolean') return value;
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false;
    }
    return defaultValue;
  }, []);

  const toIntOrNull = useCallback((value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const computeFinalResize = useCallback(() => {
    const sourceWidth = selectionDimensions.width;
    const sourceHeight = selectionDimensions.height;

    if (!sourceWidth || !sourceHeight) {
      return { width: 0, height: 0, applied: false };
    }

    const targetWidth = resizeWidth && resizeWidth > 0 ? resizeWidth : null;
    const targetHeight = resizeHeight && resizeHeight > 0 ? resizeHeight : null;

    if (!targetWidth && !targetHeight) {
      return { width: sourceWidth, height: sourceHeight, applied: false };
    }

    if (!maintainAspectRatio) {
      return {
        width: targetWidth || sourceWidth,
        height: targetHeight || sourceHeight,
        applied: true
      };
    }

    const aspectRatio = sourceWidth / sourceHeight;

    if (targetWidth && targetHeight) {
      const scaleWidth = targetWidth / sourceWidth;
      const scaleHeight = targetHeight / sourceHeight;
      const scale = Math.min(scaleWidth, scaleHeight);
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      return { width, height, applied: true };
    }

    if (targetWidth) {
      const height = Math.max(1, Math.round(targetWidth / aspectRatio));
      return { width: targetWidth, height, applied: true };
    }

    const width = Math.max(1, Math.round(targetHeight * aspectRatio));
    return { width, height: targetHeight, applied: true };
  }, [selectionDimensions, resizeWidth, resizeHeight, maintainAspectRatio]);

  // Helper functions for canvas operations
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImagePath(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          // Reset crop coordinates when new image is loaded
          setCropCoords({ x1: 0, y1: 0, x2: 100, y2: 100 });
          setSelectionDimensions({ width: 0, height: 0 });
          setCroppedPreview(null);
          setResizedPreview(null);
          setResizeEnabled(false);
          setResizeWidth(null);
          setResizeHeight(null);
          setMaintainAspectRatio(true);
          hasManualResizeRef.current = false;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const getMousePos = (canvas, evt) => {
    const rect = canvas.getBoundingClientRect();
    // Calculate the scaling factor between displayed size and actual canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (evt.clientX - rect.left) * scaleX,
      y: (evt.clientY - rect.top) * scaleY
    };
  };

  const canvasToImageCoords = (canvasX, canvasY) => {
    const iX = Math.round((canvasX - imageOffset.x) / imageScale);
    const iY = Math.round((canvasY - imageOffset.y) / imageScale);
    return { x: iX, y: iY };
  };

  const handleMouseDown = (e) => {
    if (!image) return;
    const canvas = canvasRef.current;
    const mousePos = getMousePos(canvas, e);
    setStartPoint(mousePos);
    setIsDrawing(true);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !image || !startPoint) return;
    
    const canvas = canvasRef.current;
    const mousePos = getMousePos(canvas, e);
    
    // Convert canvas coordinates to image coordinates
    const startImageCoords = canvasToImageCoords(startPoint.x, startPoint.y);
    const endImageCoords = canvasToImageCoords(mousePos.x, mousePos.y);
    
    // Update crop coordinates
    const nx1 = Math.min(startImageCoords.x, endImageCoords.x);
    const ny1 = Math.min(startImageCoords.y, endImageCoords.y);
    const nx2 = Math.max(startImageCoords.x, endImageCoords.x);
    const ny2 = Math.max(startImageCoords.y, endImageCoords.y);
    
    setCropCoords({ x1: nx1, y1: ny1, x2: nx2, y2: ny2 });
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleResizeToggle = (event) => {
    const enabled = event.target.checked;
    setResizeEnabled(enabled);

    if (enabled) {
      const hasExistingValues = resizeWidth !== null || resizeHeight !== null;
      hasManualResizeRef.current = hasExistingValues;
      if (!hasExistingValues && selectionDimensions.width && selectionDimensions.height) {
        setResizeWidth(selectionDimensions.width);
        setResizeHeight(selectionDimensions.height);
      }
    } else {
      hasManualResizeRef.current = Boolean(resizeWidth || resizeHeight);
      setResizedPreview(croppedPreview);
    }
  };

  const handleResizeWidthChange = (event) => {
    const { value } = event.target;
    hasManualResizeRef.current = true;

    if (value === '') {
      setResizeWidth(null);
      return;
    }

    const numericValue = parseInt(value, 10);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      setResizeWidth(numericValue);
    } else {
      setResizeWidth(null);
    }
  };

  const handleResizeHeightChange = (event) => {
    const { value } = event.target;
    hasManualResizeRef.current = true;

    if (value === '') {
      setResizeHeight(null);
      return;
    }

    const numericValue = parseInt(value, 10);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      setResizeHeight(numericValue);
    } else {
      setResizeHeight(null);
    }
  };

  const handleMaintainAspectChange = (event) => {
    setMaintainAspectRatio(event.target.checked);
  };

  // Canvas drawing effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    
    // Calculate scale and offset to fit image in canvas
    const scaleX = canvasWidth / image.width;
    const scaleY = canvasHeight / image.height;
    const scale = Math.min(scaleX, scaleY);
    
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    const offsetX = (canvasWidth - scaledWidth) / 2;
    const offsetY = (canvasHeight - scaledHeight) / 2;
    
    setImageScale(scale);
    setImageOffset({ x: offsetX, y: offsetY });
    
    // Draw image
    ctx.drawImage(image, offsetX, offsetY, scaledWidth, scaledHeight);
    
    // Draw crop rectangle
    if (cropCoords.x1 !== cropCoords.x2 || cropCoords.y1 !== cropCoords.y2) {
      const cx1 = cropCoords.x1 * scale + offsetX;
      const cy1 = cropCoords.y1 * scale + offsetY;
      const cx2 = cropCoords.x2 * scale + offsetX;
      const cy2 = cropCoords.y2 * scale + offsetY;
      
      // Draw semi-transparent overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Clear the crop area
      ctx.clearRect(cx1, cy1, cx2 - cx1, cy2 - cy1);
      
      // Redraw the cropped area of the image
      ctx.drawImage(
        image,
        cropCoords.x1, cropCoords.y1, cropCoords.x2 - cropCoords.x1, cropCoords.y2 - cropCoords.y1,
        cx1, cy1, cx2 - cx1, cy2 - cy1
      );
      
      // Draw crop rectangle border
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 2;
      ctx.strokeRect(cx1, cy1, cx2 - cx1, cy2 - cy1);
    }
  }, [image, cropCoords, imageScale, imageOffset]);

  useEffect(() => {
    if (!image) {
      setSelectionDimensions(prev => {
        if (prev.width === 0 && prev.height === 0) {
          return prev;
        }
        return { width: 0, height: 0 };
      });
      setCroppedPreview(null);
      setResizedPreview(null);
      return;
    }

    if (isDrawing) {
      return;
    }

    const width = x2 - x1;
    const height = y2 - y1;

    if (width <= 0 || height <= 0) {
      setSelectionDimensions(prev => {
        if (prev.width === 0 && prev.height === 0) {
          return prev;
        }
        return { width: 0, height: 0 };
      });
      setCroppedPreview(null);
      if (!resizeEnabled) {
        setResizedPreview(null);
      }
      return;
    }

    setSelectionDimensions(prev => {
      if (prev.width === width && prev.height === height) {
        return prev;
      }
      return { width, height };
    });

    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const ctx = offscreenCanvas.getContext('2d');

    ctx.drawImage(
      image,
      x1,
      y1,
      width,
      height,
      0,
      0,
      width,
      height
    );

    const dataUrl = offscreenCanvas.toDataURL('image/jpeg', 0.92);
    setCroppedPreview(dataUrl);
    if (!resizeEnabled) {
      setResizedPreview(dataUrl);
    }
  }, [image, x1, y1, x2, y2, isDrawing, resizeEnabled]);

  useEffect(() => {
    if (!selectionDimensions.width || !selectionDimensions.height) {
      setResizeWidth(null);
      setResizeHeight(null);
      return;
    }

    if (!hasManualResizeRef.current) {
      setResizeWidth(prev => (prev === selectionDimensions.width ? prev : selectionDimensions.width));
      setResizeHeight(prev => (prev === selectionDimensions.height ? prev : selectionDimensions.height));
    }
  }, [selectionDimensions.width, selectionDimensions.height]);

  useEffect(() => {
    if (!image) {
      setResizedPreview(null);
      return;
    }

    if (!croppedPreview || !selectionDimensions.width || !selectionDimensions.height) {
      if (!resizeEnabled) {
        setResizedPreview(croppedPreview);
      }
      return;
    }

    if (!resizeEnabled) {
      setResizedPreview(croppedPreview);
      return;
    }

    const { width: targetWidth, height: targetHeight } = computeFinalResize();

    if (!targetWidth || !targetHeight) {
      setResizedPreview(null);
      return;
    }

    const resizeCanvas = document.createElement('canvas');
    resizeCanvas.width = targetWidth;
    resizeCanvas.height = targetHeight;
    const ctx = resizeCanvas.getContext('2d');

    ctx.drawImage(
      image,
      x1,
      y1,
      selectionDimensions.width,
      selectionDimensions.height,
      0,
      0,
      targetWidth,
      targetHeight
    );

    setResizedPreview(resizeCanvas.toDataURL('image/jpeg', 0.92));
  }, [
    image,
    croppedPreview,
    resizeEnabled,
    computeFinalResize,
    selectionDimensions.width,
    selectionDimensions.height,
    x1,
    y1
  ]);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    ws
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'cropimage',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return true;
      
      const currentX1 = Number(cropCoords?.x1) || 0;
      const currentY1 = Number(cropCoords?.y1) || 0;
      const currentX2 = Number(cropCoords?.x2) || 0;
      const currentY2 = Number(cropCoords?.y2) || 0;
      const currentTagName = String(tagName || 'cropped_image');
      const currentResizeEnabled = Boolean(resizeEnabled);
      const currentResizeWidth = currentResizeEnabled ? (Number(resizeWidth) || 0) : 0;
      const currentResizeHeight = currentResizeEnabled ? (Number(resizeHeight) || 0) : 0;
      const currentMaintainAspect = Boolean(maintainAspectRatio);
      
      const initialImagePath = String(initialData.imagePath || '');
      const initialX1 = Number(initialData.x1) || 0;
      const initialY1 = Number(initialData.y1) || 0;
      const initialX2 = Number(initialData.x2) || 0;
      const initialY2 = Number(initialData.y2) || 0;
      const initialTagName = String(initialData.tagName || 'cropped_image');
      const initialResizeEnabled = coerceBoolean(initialData.resizeEnabled, false);
      const initialResizeWidth = initialResizeEnabled ? (Number(initialData.resizeWidth) || 0) : 0;
      const initialResizeHeight = initialResizeEnabled ? (Number(initialData.resizeHeight) || 0) : 0;
      const initialMaintainAspect = coerceBoolean(initialData.maintainAspectRatio, true);

      const maintainAspectMatches =
        (!initialResizeEnabled && !currentResizeEnabled) ||
        initialMaintainAspect === currentMaintainAspect;
      
      return (
        initialImagePath === imagePath &&
        initialX1 === currentX1 &&
        initialY1 === currentY1 &&
        initialX2 === currentX2 &&
        initialY2 === currentY2 &&
        initialTagName === currentTagName &&
        initialResizeEnabled === currentResizeEnabled &&
        initialResizeWidth === currentResizeWidth &&
        initialResizeHeight === currentResizeHeight &&
        maintainAspectMatches
      );
    },
    customSaveData: () => {
      const tag = String(tagName || 'cropped_image');
      const resizeData = {
        resizeEnabled: Boolean(resizeEnabled),
        resizeWidth: resizeEnabled ? (resizeWidth || null) : null,
        resizeHeight: resizeEnabled ? (resizeHeight || null) : null,
        maintainAspectRatio: Boolean(maintainAspectRatio)
      };
      
      return {
        imagePath,
        x1: Number(cropCoords?.x1) || 0,
        y1: Number(cropCoords?.y1) || 0,
        x2: Number(cropCoords?.x2) || 0,
        y2: Number(cropCoords?.y2) || 0,
        tagName: tag,
        resizeEnabled: resizeData.resizeEnabled,
        resizeWidth: resizeData.resizeWidth,
        resizeHeight: resizeData.resizeHeight,
        maintainAspectRatio: resizeData.maintainAspectRatio
      };
    },
    loadDataFunction: (data) => {
      let customData = null;
      if (data?.imagePath !== undefined || data?.x1 !== undefined) {
        customData = data;
      } else if (data?.customData?.customData) {
        customData = data.customData.customData;
      } else if (data?.customData) {
        customData = data.customData;
      }
      
      if (customData) {
        if (customData.imagePath !== undefined && customData.imagePath !== null) {
          setImagePath(String(customData.imagePath));
        }
        
        if (customData.x1 !== undefined && customData.x1 !== null) {
          setCropCoords(prev => ({ ...prev, x1: Number(customData.x1) || 0 }));
        }
        if (customData.y1 !== undefined && customData.y1 !== null) {
          setCropCoords(prev => ({ ...prev, y1: Number(customData.y1) || 0 }));
        }
        if (customData.x2 !== undefined && customData.x2 !== null) {
          setCropCoords(prev => ({ ...prev, x2: Number(customData.x2) || 0 }));
        }
        if (customData.y2 !== undefined && customData.y2 !== null) {
          setCropCoords(prev => ({ ...prev, y2: Number(customData.y2) || 0 }));
        }
        
        if (customData.tagName !== undefined && customData.tagName !== null) {
          setTagName(String(customData.tagName));
        }

        const loadedResizeEnabled = coerceBoolean(customData.resizeEnabled, false);
        const loadedMaintainAspect = coerceBoolean(customData.maintainAspectRatio, true);
        const loadedResizeWidth = toIntOrNull(customData.resizeWidth);
        const loadedResizeHeight = toIntOrNull(customData.resizeHeight);

        setResizeEnabled(loadedResizeEnabled);
        setMaintainAspectRatio(loadedMaintainAspect);
        setResizeWidth(loadedResizeWidth);
        setResizeHeight(loadedResizeHeight);
        hasManualResizeRef.current = Boolean(loadedResizeWidth || loadedResizeHeight);
      } else {
        setResizeEnabled(false);
        setMaintainAspectRatio(true);
        setResizeWidth(null);
        setResizeHeight(null);
        hasManualResizeRef.current = false;
      }
    }
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    ws,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/\/+$/, "");
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        folderPathClean = folderPathClean + separator;
        setImagePath(folderPathClean + "{filename}.{file_extension}");
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });

  const customDataTags = [
    { key: 'tag_imageWidth', label: t('crop_image.image_width'), value: 'imageWidth', description: t('crop_image.image_width_desc') },
    { key: 'tag_imageHeight', label: t('crop_image.image_height'), value: 'imageHeight', description: t('crop_image.image_height_desc') }
  ];

  const finalResizeDimensions = computeFinalResize();

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'tags') {
      imagePathRef.current?.insertTag(value);
    } else if (menuType === 'tagName') {
      tagNameRef.current?.insertTag(value);
    }
    handleMainTagMenuClose();
  };

  const handleBrowseClick = () => {
    setBrowsingDisabled(true);
    handleBrowseDirectory();
  };

  return (
    <ActionSettingsLayout
      title={t('crop_image.title')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      customDataTags={customDataTags}
      tagsDropdownProps={{
        tags: tags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMainTagMenuClose,
        menuType: menuType,
        additionalTags: customDataTags
      }}
    >
      {/* Image Path Configuration */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #ff6b3510',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#ff6b35',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(255, 107, 53, 0.2)',
              }}>
                {t('crop_image.where_label')}
              </Box>
              {t('crop_image.where_to_load')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fff7f5',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(255, 107, 53, 0.05)',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'stretch',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#ff6b35',
              boxShadow: '0 8px 20px rgba(255, 107, 53, 0.1)',
            },
          }}>
            <Box sx={{ flex: 1 }}>
              <TagEditor
                    caseid={caseid}
                ref={imagePathRef}
                value={imagePath}
                onChange={setImagePath}
                placeholder={t('crop_image.image_path_placeholder')}
                singleLine
                onTagClick={(e) => handleMenuOpen(e, 'tags')}
                tags={tags}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleBrowseClick}
              disabled={browsingDisabled}
              sx={{
                borderRadius: '0 12px 12px 0',
                backgroundColor: '#ff6b35',
                color: 'white',
                m: 0,
                px: 2.5,
                '&:hover': {
                  backgroundColor: '#e55a2b',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#c0c0d0',
                  color: 'white',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <BrowseIcon />
            </Button>
          </Box>

          <Box sx={{
            p: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            backgroundColor: '#fff7f5',
            borderRadius: '8px',
            border: '1px solid #eaeaf0',
          }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#ff6b35',
              fontSize: '0.85rem',
              mr: 1,
              display: 'flex',
              alignItems: 'center',
            }}>
              {t('quick_tags')}:
            </Typography>
            {[
              { label: t('filename'), value: 'filename' },
              { label: t('extension'), value: 'file_extension' },
              { label: t('file_folder_path'), value: 'file_folder_path' },
            ].map(tag => (
              <Chip
                key={tag.value}
                label={tag.label}
                onClick={() => {
                  if (imagePathRef.current) {
                    imagePathRef.current.focus();
                    setTimeout(() => {
                      imagePathRef.current.insertTag(tag.value);
                    }, 10);
                  }
                }}
                size="small"
                sx={{
                  backgroundColor: '#fff5e6',
                  color: '#ff6b35',
                  borderColor: '#ff6b3530',
                  border: '1px solid',
                  '&:hover': {
                    backgroundColor: '#ffe8d1',
                    boxShadow: '0 2px 6px rgba(255, 107, 53, 0.15)',
                  },
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Crop Configuration */}
      <Card sx={{
        mb: 0, 
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #ff6b3510',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#ff6b35',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(255, 107, 53, 0.2)',
              }}>
                {t('crop_image.what_label')}
              </Box>
              {t('crop_image.what_to_do')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fff7f5',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
            <AlertTitle>{t('crop_image.how_it_works')}</AlertTitle>
            {t('crop_image.how_it_works_desc')}
          </Alert>

          {/* Tag Name Input */}
          <Box sx={{ mb: 3 }}>
            <TagEditor
                    caseid={caseid}
              ref={tagNameRef}
              value={tagName}
              onChange={setTagName}
              placeholder="cropped_image"
              label={t('crop_image.tag_name_label')}
              singleLine
              tags={tags}
              onTagClick={(e) => handleMenuOpen(e, 'tagName')}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {t('crop_image.tag_name_help')}
            </Typography>
          </Box>

          {/* Image Preview and Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ff6b35', fontWeight: 600 }}>
              {t('crop_image.preview_title', 'Image Preview')}
            </Typography>
            
            {/* Image Upload */}
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleImageSelect}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  sx={{
                    borderColor: '#ff6b35',
                    color: '#ff6b35',
                    '&:hover': {
                      borderColor: '#e55a2b',
                      backgroundColor: '#fff5e6',
                    },
                  }}
                >
                  {t('crop_image.select_preview_image', 'Select Image for Preview')}
                </Button>
              </label>
              {selectedImagePath && (
                <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                  {t('crop_image.selected_image', 'Selected')}: {selectedImagePath}
                </Typography>
              )}
            </Box>

            {/* Canvas Preview */}
            {image && (
              <Box sx={{
                border: '2px solid #ff6b35',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f9f9f9',
                mb: 2,
              }}>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={400}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    cursor: isDrawing ? 'crosshair' : 'pointer'
                  }}
                />
                <Box sx={{ p: 1, backgroundColor: '#fff7f5', borderTop: '1px solid #ff6b35' }}>
                  <Typography variant="body2" sx={{ color: '#ff6b35', fontWeight: 600 }}>
                    {t('crop_image.canvas_instructions', 'Click and drag to select crop area. Coordinates will update automatically.')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Crop Coordinates */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ff6b35', fontWeight: 600 }}>
              {t('crop_image.crop_coordinates')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {t('crop_image.coordinates_desc')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label={t('crop_image.x1_label')}
                type="number"
                value={cropCoords.x1}
                onChange={(e) => setCropCoords(prev => ({ ...prev, x1: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ff6b35',
                  },
                }}
              />
              <TextField
                label={t('crop_image.y1_label')}
                type="number"
                value={cropCoords.y1}
                onChange={(e) => setCropCoords(prev => ({ ...prev, y1: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ff6b35',
                  },
                }}
              />
              <TextField
                label={t('crop_image.x2_label')}
                type="number"
                value={cropCoords.x2}
                onChange={(e) => setCropCoords(prev => ({ ...prev, x2: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ff6b35',
                  },
                }}
              />
              <TextField
                label={t('crop_image.y2_label')}
                type="number"
                value={cropCoords.y2}
                onChange={(e) => setCropCoords(prev => ({ ...prev, y2: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ff6b35',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ff6b35',
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ff6b35', fontWeight: 600 }}>
              {t('crop_image.output_preview_title', 'Cropped Output')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {t('crop_image.output_preview_desc', 'Review the image data that will be saved to the selected tag.')}
            </Typography>
            {croppedPreview ? (
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: 480
              }}>
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444' }}>
                    {t('crop_image.cropped_preview_label', 'Cropped selection')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {t('crop_image.cropped_preview_size', {
                      width: selectionDimensions.width || 0,
                      height: selectionDimensions.height || 0
                    })}
                  </Typography>
                </Box>
                <Box
                  component="img"
                  src={croppedPreview}
                  alt={t('crop_image.cropped_preview_alt', 'Preview of cropped selection')}
                  sx={{ width: '100%', display: 'block' }}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                {t('crop_image.preview_unavailable', 'Draw a crop area above to generate a preview.')}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ff6b35', fontWeight: 600 }}>
              {t('crop_image.resize_section_title', 'Resize Options')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {t('crop_image.resize_section_desc', 'Optionally scale the cropped image before saving it to the tag.')}
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={resizeEnabled}
                  onChange={handleResizeToggle}
                  color="primary"
                />
              )}
              label={t('crop_image.resize_toggle_label', 'Resize cropped image before saving')}
              sx={{ mb: resizeEnabled ? 2 : 0 }}
            />

            {resizeEnabled && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  mb: 2
                }}>
                  <TextField
                    label={t('crop_image.resize_width_label', 'Target width')}
                    type="number"
                    value={resizeWidth ?? ''}
                    onChange={handleResizeWidthChange}
                    inputProps={{ min: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">px</InputAdornment>
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b35',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b35',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#ff6b35',
                      },
                    }}
                  />
                  <TextField
                    label={t('crop_image.resize_height_label', 'Target height')}
                    type="number"
                    value={resizeHeight ?? ''}
                    onChange={handleResizeHeightChange}
                    inputProps={{ min: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">px</InputAdornment>
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b35',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b35',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#ff6b35',
                      },
                    }}
                  />
                </Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={maintainAspectRatio}
                      onChange={handleMaintainAspectChange}
                      color="primary"
                    />
                  )}
                  label={t('crop_image.maintain_aspect_label', 'Maintain aspect ratio')}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                  {t('crop_image.resize_hint', 'Leave a field blank to keep that dimension from the cropped selection.')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#444', fontWeight: 600 }}>
                  {t('crop_image.resize_result_label', {
                    width: finalResizeDimensions.width || selectionDimensions.width || 0,
                    height: finalResizeDimensions.height || selectionDimensions.height || 0
                  })}
                </Typography>
              </Box>
            )}

            {resizeEnabled && resizedPreview && finalResizeDimensions.width > 0 && finalResizeDimensions.height > 0 && (
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: 480
              }}>
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#444' }}>
                    {t('crop_image.resized_preview_label', 'Resized preview')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {t('crop_image.resize_preview_size', {
                      width: finalResizeDimensions.width,
                      height: finalResizeDimensions.height
                    })}
                  </Typography>
                </Box>
                <Box
                  component="img"
                  src={resizedPreview}
                  alt={t('crop_image.resized_preview_alt', 'Preview of resized cropped image')}
                  sx={{ width: '100%', display: 'block' }}
                />
              </Box>
            )}
          </Box>

          {/* Info about image path */}
          <Alert severity="warning" sx={{ borderRadius: '8px' }}>
            <AlertTitle>{t('crop_image.image_path_note_title', 'Image Path Note')}</AlertTitle>
            {t('crop_image.image_path_note', 'If no image path is specified above, the system will use the source file of each time event (the file that triggered the event) as the image to crop.')}
          </Alert>
        </CardContent>
      </Card>
      
      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCropImage);
